import type { WorldData } from './types';
import type { EntityRef, EntityKind } from './editor-model';
export type MapIssue = {
    severity: 'error' | 'warning';
    message: string;
    ref?: EntityRef;
};
export const finitePoint = (a: unknown): a is [
    number,
    number,
    number
] => Array.isArray(a) && a.length === 3 && a.every(v => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) < 10000);
const validState = (a: unknown) => Array.isArray(a) && a.length === 40 && a.every(v => typeof v === 'number' && Number.isFinite(v));
export function inspectMap(value: unknown): MapIssue[] {
    const issues: MapIssue[] = [];
    const add = (message: string, kind?: EntityKind, id?: unknown, severity: 'error' | 'warning' = 'error') => issues.push({ message, severity, ...(kind ? { ref: { kind, id: String(id) } } : {}) });
    if (!value || typeof value !== 'object')
        return [{ severity: 'error', message: 'Map must be an object.' }];
    const m = value as WorldData;
    if (m.version !== 1)
        add('Unsupported map version.');
    if (!finitePoint(m.spawn))
        add('Spawn must contain three finite coordinates.');
    if (!Number.isInteger(m.physicsVersion))
        add('Physics version must be an integer.');
    for (const k of ['objects', 'regions', 'pois', 'routes', 'fruit', 'npcs', 'activities', 'volumes'] as const)
        if (!Array.isArray(m[k]))
            add(`${k} must be an array.`);
    if (m.tagRegistry !== undefined && (!Array.isArray(m.tagRegistry) || m.tagRegistry.length > 1000 || m.tagRegistry.some(t => typeof t !== 'string' || t.length > 80)))
        add('Invalid tag registry.');
    if (m.checkpoints !== undefined && !Array.isArray(m.checkpoints))
        add('Checkpoints must be an array.');
    if (issues.length)
        return issues;
    const groups = [['object', m.objects], ['region', m.regions], ['poi', m.pois], ['volume', m.volumes], ['checkpoint', m.checkpoints || []], ['activity', m.activities]] as const;
    if (m.objects.length > 2000)
        add('Map exceeds 2,000 traversal objects.');
    if (!m.regions.length)
        add('Keep at least one region.');
    const uuids = new Set<string>();
    for (const [kind, list] of groups) {
        if (list.length > 2000) {
            add(`Too many ${kind} entries.`);
            continue;
        }
        const ids = new Set();
        for (const e of list) {
            if (!e || typeof e !== 'object') {
                add(`Invalid ${kind} entry.`);
                continue;
            }
            if ((kind === 'object' ? (!Number.isInteger(e.id) || Number(e.id) < 0 || Number(e.id) > 1e7) : (typeof e.id !== 'string' || !e.id.length || e.id.length > 100)) || ids.has(e.id))
                add(`Duplicate or invalid ${kind} ID ${e.id}.`, kind, e.id);
            ids.add(e.id);
            if (typeof e.name !== 'string' || e.name.length > 160)
                add(`Invalid ${kind} name.`, kind, e.id);
            if (e.uuid !== undefined && (typeof e.uuid !== 'string' || !e.uuid || uuids.has(e.uuid)))
                add(`Duplicate or invalid UUID.`, kind, e.id);
            if (e.uuid)
                uuids.add(e.uuid);
            if (e.tags !== undefined && (!Array.isArray(e.tags) || e.tags.length > 80 || e.tags.some(t => typeof t !== 'string' || t.length > 80)))
                add('Tags must be a short list of text values.', kind, e.id);
            if ('priority' in e && e.priority !== undefined && (!Number.isFinite(e.priority) || Math.abs(e.priority) > 1000))
                add('Priority must be between -1,000 and 1,000.', kind, e.id);
        }
    }
    // Stop before dereferencing malformed collection entries.
    if (groups.some(([, list]) => list.some(e => !e || typeof e !== 'object')) || groups.some(([, list]) => list.length > 2000))
        return issues;
    const regionIds = new Set(m.regions.map(r => r.id)), volumeIds = new Set(m.volumes.map(v => v.id)), poiIds = new Set(m.pois.map(p => p.id));
    for (const o of m.objects) {
        if (!Number.isInteger(o.type) || o.type < 0 || o.type > 7 || !finitePoint(o.a) || !finitePoint(o.b) || !Number.isFinite(o.radius) || o.radius <= 0 || o.radius > 300 || !Array.isArray(o.tags))
            add(`Invalid traversal geometry for ${o.name}.`, 'object', o.id);
        if (!regionIds.has(o.region))
            add('Object references a missing region.', 'object', o.id);
    }
    for (const r of m.regions) {
        if (!finitePoint(r.center) || !Number.isFinite(r.radius) || r.radius <= 0 || r.radius > 10000)
            add('Invalid region bounds.', 'region', r.id);
        if (r.parent && !regionIds.has(r.parent))
            add('Parent region is missing.', 'region', r.id);
        if (r.volume && !volumeIds.has(r.volume))
            add('Region volume is missing.', 'region', r.id);
        const seen = new Set<string>();
        let current: typeof r | undefined = r;
        while (current) {
            if (seen.has(current.id)) {
                add('Region hierarchy contains a cycle.', 'region', r.id);
                break;
            }
            seen.add(current.id);
            current = m.regions.find(p => p.id === current!.parent);
        }
    }
    for (const p of m.pois) {
        if (!finitePoint(p.position) || !Number.isFinite(p.radius) || p.radius <= 0 || typeof p.description !== 'string' || !regionIds.has(p.region))
            add('Invalid POI or region reference.', 'poi', p.id);
        if (p.requires && (!Array.isArray(p.requires) || p.requires.some(id => !poiIds.has(id) || id === p.id)))
            add('POI prerequisites reference a missing discovery or itself.', 'poi', p.id);
        if (p.vista && (!finitePoint(p.vista.position) || !finitePoint(p.vista.target) || !Number.isFinite(p.vista.fov) || p.vista.fov < 20 || p.vista.fov > 120))
            add('Invalid vista camera.', 'poi', p.id);
    }
    for (const c of m.checkpoints || [])
        if (!finitePoint(c.position) || !regionIds.has(c.region) || (c.state !== undefined && !validState(c.state)))
            add('Invalid checkpoint or recorded state.', 'checkpoint', c.id);
    const completed = new Set<string>(), visiting = new Set<string>(), cycles = new Set<string>();
    const visit = (id: string): boolean => { if (visiting.has(id)) {
        cycles.add(id);
        return true;
    } if (completed.has(id))
        return cycles.has(id); visiting.add(id); const v = m.volumes.find(v => v.id === id); let cyclic = false; if (Array.isArray(v?.members))
        for (const child of v!.members!)
            cyclic = visit(child) || cyclic; visiting.delete(id); completed.add(id); if (cyclic)
        cycles.add(id); return cyclic; };
    for (const v of m.volumes)
        visit(v.id);
    for (const v of m.volumes) {
        if (!finitePoint(v.position) || !finitePoint(v.size) || v.size.some(s => s <= 0) || !regionIds.has(v.region))
            add('Invalid volume bounds or region.', 'volume', v.id);
        if (!['sphere', 'box', 'capsule', 'cylinder', 'prism', 'spline', 'convex', 'composite'].includes(v.shape))
            add('Unknown volume shape.', 'volume', v.id);
        if (['prism', 'spline', 'convex'].includes(v.shape)) {
            const min = v.shape === 'spline' ? 2 : v.shape === 'prism' ? 3 : 4;
            if (!Array.isArray(v.points) || v.points.length < min || v.points.length > 32 || v.points.some(p => !finitePoint(p)))
                add(`This shape needs ${min}–32 finite local points.`, 'volume', v.id);
            else if (v.shape === 'prism') {
                let valid = true, area = 0;
                const pts = v.points;
                for (let i = 0; i < pts.length; i++) {
                    const a = pts[i], b = pts[(i + 1) % pts.length];
                    area += a[0] * b[2] - b[0] * a[2];
                    let side = 0;
                    for (const p of pts) {
                        const cross = (b[0] - a[0]) * (p[2] - a[2]) - (b[2] - a[2]) * (p[0] - a[0]);
                        if (Math.abs(cross) < 1e-7)
                            continue;
                        const sign = Math.sign(cross);
                        if (side && side !== sign)
                            valid = false;
                        side = sign;
                    }
                }
                if (!valid || Math.abs(area) < 1e-5)
                    add('Prism points must trace a non-degenerate convex polygon in order.', 'volume', v.id);
            }
            else if (v.shape === 'convex') {
                const [a, ...ps] = v.points;
                let determinant = 0;
                for (const b of ps)
                    for (const c of ps)
                        for (const d of ps) {
                            const u = b.map((x, i) => x - a[i]), v = c.map((x, i) => x - a[i]), w = d.map((x, i) => x - a[i]);
                            determinant = Math.max(determinant, Math.abs(u[0] * (v[1] * w[2] - v[2] * w[1]) - u[1] * (v[0] * w[2] - v[2] * w[0]) + u[2] * (v[0] * w[1] - v[1] * w[0])));
                        }
                if (determinant < 1e-5)
                    add('Convex points must enclose a three-dimensional space.', 'volume', v.id);
            }
        }
        if (v.shape === 'capsule' && finitePoint(v.size) && v.size[1] < 2 * v.size[0])
            add('Capsule height must be at least twice its radius.', 'volume', v.id);
        if (v.shape === 'composite' && (!Array.isArray(v.members) || !v.members.length || v.members.length > 32 || v.members.some(id => !volumeIds.has(id)) || cycles.has(v.id)))
            add('Composite needs existing members without cycles.', 'volume', v.id);
    }
    const routeIds = new Set<string>();
    for (const r of m.routes) {
        if (!r || typeof r.id !== 'string' || routeIds.has(r.id) || typeof r.name !== 'string' || !Array.isArray(r.points) || r.points.length > 36000 || r.points.some(p => !finitePoint(p))) {
            add('Invalid or duplicate route.');
            continue;
        }
        routeIds.add(r.id);
        if (r.inputs !== undefined && (!Array.isArray(r.inputs) || r.inputs.length > 36000 || r.inputs.some(i => !i || ![i.x, i.z, i.buttons, i.assist].every(Number.isFinite) || Math.abs(i.x) > 1.2 || Math.abs(i.z) > 1.2 || !Number.isInteger(i.buttons) || i.buttons < 0 || i.buttons > 31 || !Number.isInteger(i.assist) || i.assist < 0 || i.assist > 2)))
            add(`Invalid recorded inputs: ${r.name}.`);
        for (const state of [r.initialState, r.finalState])
            if (state !== undefined && !validState(state))
                add(`Invalid recorded state: ${r.name}.`);
        if (r.trace !== undefined && (!Array.isArray(r.trace) || r.trace.length > 36000 || r.trace.some(s => !validState(s))))
            add(`Invalid route trace: ${r.name}.`);
    }
    if (m.routes.length > 500 || m.npcs.length > 32 || m.fruit.length > 10000)
        add('Map exceeds route, NPC or collectible limits.');
    for (const f of m.fruit)
        if (!f || typeof f.id !== 'string' || !finitePoint(f.position))
            add('Invalid fruit.');
    for (const n of m.npcs)
        if (!n || typeof n.id !== 'string' || !routeIds.has(n.route))
            add('NPC references a missing route.');
    for (const a of m.activities)
        if (!Array.isArray(a.gates) || a.gates.length < 2 || a.gates.length > 200 || a.gates.some(p => !finitePoint(p)) || !Number.isFinite(a.radius) || a.radius <= 0 || !Array.isArray(a.medals) || a.medals.length !== 3 || a.medals.some((m, i) => !Number.isFinite(m) || m <= 0 || (i > 0 && m < a.medals[i - 1])))
            add('Activity needs ordered gates, a positive radius and three increasing medal times.', 'activity', a.id);
    return issues.slice(0, 100);
}
